import {
  createTag, addSitesToTag, getTagById, bulkAssociateTagsSites,
} from './api/tag';
import { getSiteInfo } from './api/site';

export const handleCreateAndAssociateTag = async (tagName, orgId, siteIds, setIsSubmitting, handleClose) => {
  if (!tagName.trim()) {
    throw new Error('Tag name is required.');
  }

  // Creates the tag
  const response = await createTag({
    tag: {
      name: tagName,
    },
  }, orgId);

  if (!response.ok) {
    if (response.json) {
      const errorData = await response.json();
      setIsSubmitting(false);
      throw new Error(errorData.error || 'Failed to create tag');
    }
    setIsSubmitting(false);
    throw new Error('Failed to create tag');
  }

  const data = await response.json();

  // Associates the tag with the site
  const associationResponse = await addSitesToTag(data.tag.id, orgId, siteIds);
  if (!associationResponse.ok) {
    throw new Error('Failed to associate tag with site');
  }

  setIsSubmitting(false);
  handleClose();
  return data.tag;
};

export const handleCreatedNotAssociatedTag = (tag, onCreate, orgId, showToast, setInlineError, setIsSubmitting, handleClose) => {
  createTag({ tag }, orgId)
    .then((response) => {
      if (!response.ok) {
        if (response.json) {
          return response.json().then((errorData) => {
            throw new Error(errorData.error || 'Failed to create tag');
          });
        }
        throw new Error('Failed to create tag');
      }
      return response.json();
    })
    .then((data) => {
      onCreate(data.tag);
      showToast({ message: 'Tag has been created.', type: 'success' });
      setIsSubmitting(false);
      handleClose();
    })
    .catch((err) => {
      setIsSubmitting(false);
      setInlineError(err.message);
    });
};

export const handleAssociateExistingTag = async (tagId, orgId, siteIds) => {
  if (!tagId || !orgId || !siteIds) {
    throw new Error('Tag ID, Organization ID, and Site ID are required.');
  }

  // Associates the tag with the site
  const associationResponse = await addSitesToTag(tagId, orgId, siteIds);
  if (!associationResponse.ok) {
    throw new Error('Failed to associate tag with site');
  }

  // Fetch the tag object
  const tagResponse = await getTagById(tagId, orgId);
  if (!tagResponse.ok) {
    throw new Error('Failed to fetch tag details.');
  }

  // Extract the tag object
  const { tag } = await tagResponse.json();
  return tag;
};

export const handleBulkTagsAndSitesAssociation = async (orgId, tagIds, siteIds) => {
  const associationResponse = await bulkAssociateTagsSites(orgId, tagIds, siteIds);
  if (!associationResponse.ok) {
    throw new Error('Failed to associate tag with site');
  }
};

export const fetchTagsForSites = async (siteIds, organizationId, showToast) => {
  const siteAndTags = {};

  try {
    await Promise.all(
      siteIds.map((siteId) => getSiteInfo(siteId, organizationId)
        .then((res) => res.json())
        .then((data) => {
          if (!data.site) throw new Error(data.error);
          siteAndTags[siteId] = data.site.tags;
        })),
    );

    const tagAndSites = [];
    Object.keys(siteAndTags).forEach((site) => {
      siteAndTags[site].forEach((tag) => {
        const existing = tagAndSites.find((t) => t.id === tag.id);
        if (existing) existing.sites.push(site);
        else tagAndSites.push({ ...tag, sites: [site] });
      });
    });

    return tagAndSites;
  } catch (error) {
    showToast?.({ message: error.message, type: 'error' });
    return [];
  }
};
